import axios from "axios";
import { GLOBAL_API } from "./types";
import {
  getInterfaceLanguage,
  shouldSendRequestLanguage,
} from "../i18n/requestLanguage";

export const getToken = () => localStorage.getItem("ecauthtoken");
export const getSecureToken = () => localStorage.getItem("ecsecuretoken");

let BASE_URL = GLOBAL_API;

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

axiosInstance.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  if (shouldSendRequestLanguage()) {
    const lang = getInterfaceLanguage();
    // User requests carry both language headers. X-App-Language remains the
    // explicit application preference if a proxy rewrites Accept-Language.
    if (typeof config.headers.set === "function") {
      config.headers.set("Accept-Language", lang);
      config.headers.set("X-App-Language", lang);
    } else {
      config.headers["Accept-Language"] = lang;
      config.headers["X-App-Language"] = lang;
    }
  } else {
    // Admin, superadmin and e-learning requests must not inherit a user's
    // saved language. Delete the headers instead of sending an English override.
    if (typeof config.headers.delete === "function") {
      config.headers.delete("Accept-Language");
      config.headers.delete("X-App-Language");
    } else {
      delete config.headers["Accept-Language"];
      delete config.headers["X-App-Language"];
    }
  }

  // Resolve auth at dispatch time so logout cannot leave stale Axios defaults
  // attached to a later login or public request.
  const authToken = localStorage.getItem("ecauthtoken");
  const secureToken = localStorage.getItem("ecsecuretoken");
  if (authToken && secureToken) {
    config.headers["ecauthtoken"] = authToken;
    config.headers["ecsecuretoken"] = secureToken;
  } else {
    delete config.headers["ecauthtoken"];
    delete config.headers["ecsecuretoken"];
  }
  return config;
});

//on successful response
axiosInstance.interceptors.response.use(
  (config) => {
    if (config?.headers?.ecauthtoken) {
      localStorage.setItem("ecauthtoken", config?.headers?.ecauthtoken);
    }
    if (config?.headers?.ecsecuretoken) {
      localStorage.setItem("ecsecuretoken", config?.headers?.ecsecuretoken);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const setAuthorizationToken = () => {
  var token = localStorage.getItem("ecauthtoken");
  if (token) {
    axiosInstance.defaults.headers.common["ecauthtoken"] = token;
  }

  let ecsecuretoken = localStorage.getItem("ecsecuretoken");
  if (ecsecuretoken) {
    axiosInstance.defaults.headers.common["ecsecuretoken"] = ecsecuretoken;
  }
};

export const clearAuthorizationToken = () => {
  delete axiosInstance.defaults.headers.common["ecauthtoken"];
  delete axiosInstance.defaults.headers.common["ecsecuretoken"];
};

// Send a POST request
export const postRequest = (url, data) => {
  setAuthorizationToken();
  return axiosInstance({
    method: "post",
    url,
    data,
  });
};

// Send a GET request
export const getRequest = (url) => {
  setAuthorizationToken();
  return axiosInstance({
    method: "get",
    url,
  });
};

// Send a DELETE request
export const deleteRequest = (url, obj = null) => {
  setAuthorizationToken();
  if (obj) {
    return axios.delete(url, {
      headers: {
        ecauthtoken: localStorage.getItem("ecauthtoken"),
        ecsecuretoken: localStorage.getItem("ecsecuretoken"),
      },
      data: obj,
    });
  } else {
    return axiosInstance({
      method: "delete",
      url,
      config: {
        data: obj,
      },
    });
  }
};

// Send a PUT request
export const putRequest = (url, data) => {
  setAuthorizationToken();
  return axiosInstance({
    method: "put",
    url,
    data,
  });
};
